import React, { useState } from "react";
import PropTypes from "prop-types";
import { useTable } from "react-table";
import { Table, CardBody, Card, Button, Row, Col, Label, Input } from "reactstrap";
import dayjs from "dayjs";
import NumberFormat from "react-number-format";
import useCurrentSaleStore from "stores/SaleStore";
import useCurrentDealStore from "stores/DealStore";
import useCurrentLeaseStore from "stores/LeaseStore";
import shallow from "zustand/shallow";
import {
	calculateFinalDueDate,
	calculatePayment,
	calculateFinalPayment,
	calculateFinanceCharge,
} from "features/deal/helperFunctions/SaleFunctions";
import { moneyFactorFromApr } from "features/deal/helperFunctions/LeaseFunctions";
import { DealStatus, LoanPeriod } from "constants/Constants";

/**
 * PaymentOptionsModal component renders a modal containing payment options in a table format.
 *
 * @component
 * @param {Object} props - React props.
 * @param {Function} props.toggle - Function to toggle the modal.
 * @returns {JSX.Element}
 */
const PaymentOptionsModal = ({ toggle }) => {
	const {
		loanPeriod,
		apr,
		loanDate,
		firstDueDate,
		amtFinanced,
		editTerm,
		editLoanPeriod,
		bankFee,
		saleType,
		taxRate,
	} = useCurrentSaleStore(
		(state) => ({
			loanPeriod: state.loanPeriod,
			apr: state.apr,
			loanDate: state.loanDate,
			firstDueDate: state.firstDueDate,
			amtFinanced: state.amtFinanced,
			editTerm: state.editTerm,
			editLoanPeriod: state.editLoanPeriod,
			bankFee: state.bankFee,
			deferredTax: state.deferredTax,
			saleType: state.saleType,
			taxRate: state.taxRate,
		}),
		shallow
	);

	const { type } = useCurrentDealStore(
		(state) => ({
			type: state.deal.type,
		}),
		shallow
	);

	// Lease store for lease/RTO deals
	const {
		leasePeriod,
		leaseApr,
		netCap,
		residualValue,
		capSalesTax,
		editField: editLeaseField,
	} = useCurrentLeaseStore(
		(state) => ({
			leasePeriod: state.leasePeriod,
			leaseApr: state.apr,
			netCap: state.netCap,
			residualValue: state.residualValue,
			capSalesTax: state.capSalesTax,
			editField: state.editField,
		}),
		shallow
	);

	// Determine if this is a lease or RTO deal
	const isLease = saleType === 5;
	const isRTO = saleType === 6;
	const isLeaseOrRTO = isLease || isRTO;

	// Use appropriate period based on deal type
	const currentPeriod = isLeaseOrRTO ? leasePeriod : loanPeriod;
	const currentApr = isLeaseOrRTO ? leaseApr : apr;

	// Local state for period selection in modal
	const [selectedPeriod, setSelectedPeriod] = useState(currentPeriod);

	/**
	 * Generates the term value based on loan period and row number.
	 *
	 * @param {number} rowNum - The row number.
	 * @returns {number} The generated term.
	 */
	const generateTerm = (rowNum) => {
		let term = 0;
		switch (selectedPeriod) {
			case 0: // Weekly - 52 times a year
				term += rowNum * 52;
				break;
			case 1: // Bi-Weekly - 26 times a year
				term += rowNum * 26;
				break;
			case 2: // Semi-Monthly - 24 times a year
				term += rowNum * 24;
				break;
			case 3: // Monthly - 12 times a year
				term += rowNum * 12;
				break;
			case 4: // Annually - 1 time a year
				term = rowNum;
				break;
			case 5: // Cash - N/A
				term = 1;
				break;
			case 6: // Bi-Annually - 2 times a year
				term += rowNum * 2;
				break;
			default:
				break;
		}
		return term;
	};

	/**
	 * Calculates lease payment based on term
	 * Formula: payment = (depreciation/term + rentCharge) * (1 + taxRate if capSalesTax)
	 */
	const calculateLeasePayment = (term) => {
		if (term === 0) return 0;
		const periodsPerYear = LoanPeriod[selectedPeriod]?.term || 12;
		const moneyFactor = moneyFactorFromApr(currentApr, periodsPerYear);

		const depreciation = netCap - residualValue;
		const monthlyDepreciation = depreciation / term;
		const monthlyInterest = (netCap + residualValue) * moneyFactor;
		const basePayment = monthlyDepreciation + monthlyInterest;
		const monthlyTax = capSalesTax ? basePayment * taxRate : 0;

		return basePayment + monthlyTax;
	};

	/**
	 * Calculates RTO payment based on term (no interest)
	 * Formula: payment = (depreciation/term) * (1 + taxRate if capSalesTax)
	 */
	const calculateRTOPayment = (term) => {
		if (term === 0) return 0;
		const depreciation = netCap - residualValue;
		const basePayment = depreciation / term;
		const monthlyTax = capSalesTax ? basePayment * taxRate : 0;

		return basePayment + monthlyTax;
	};

	/**
	 * Calculates finance charge for lease
	 */
	const calculateLeaseFinanceCharge = (term) => {
		if (term === 0) return 0;
		const periodsPerYear = LoanPeriod[selectedPeriod]?.term || 12;
		const moneyFactor = moneyFactorFromApr(currentApr, periodsPerYear);
		const monthlyInterest = (netCap + residualValue) * moneyFactor;
		return monthlyInterest * term;
	};

	/**
	 * Creates row data including term, payment, finance charge, and final due date.
	 *
	 * @param {number} rowNum - The row number.
	 * @returns {Object} The row data.
	 */
	const createRowData = (rowNum) => {
		const term = generateTerm(rowNum);
		const finalDueDate = dayjs(
			calculateFinalDueDate(selectedPeriod, term, firstDueDate)
		).format("MM/DD/YYYY");

		let payment, financeCharge;

		if (isRTO) {
			// RTO: No interest, simple depreciation
			payment = calculateRTOPayment(term);
			financeCharge = 0;
		} else if (isLease) {
			// Lease: Uses money factor
			payment = calculateLeasePayment(term);
			financeCharge = calculateLeaseFinanceCharge(term);
		} else {
			// Regular retail deal
			payment = calculatePayment(
				apr,
				selectedPeriod,
				term,
				amtFinanced,
				loanDate,
				firstDueDate,
				bankFee
			);
			const finalPayment = calculateFinalPayment(apr, payment, term, amtFinanced);
			financeCharge = calculateFinanceCharge(
				amtFinanced,
				payment,
				finalPayment,
				term
			);
		}

		return { term, payment, financeCharge, finalDueDate };
	};

	// Memoized row data for performance optimization
	const data = React.useMemo(
		() => Array.from({ length: 9 }, (_, i) => createRowData(i + 1)),
		// eslint-disable-next-line
		[selectedPeriod, apr, loanDate, firstDueDate, amtFinanced, bankFee, isLease, isRTO, netCap, residualValue, capSalesTax, taxRate, currentApr]
	);

	// Memoized columns configuration for React Table
	const columns = React.useMemo(
		() => [
			{
				Header: "Term",
				accessor: "term",
			},
			{
				Header: "Payment",
				accessor: "payment",
				Cell: ({ value }) => (
					<NumberFormat
						value={value}
						thousandSeparator={true}
						decimalScale={2}
						fixedDecimalScale={true}
						prefix={"$"}
						isNumericString={true}
						displayType="text"
					/>
				),
			},
			{
				Header: isRTO ? "Interest" : isLease ? "Rent Charge" : "Finance Chg.",
				accessor: "financeCharge",
				Cell: ({ value }) => (
					<NumberFormat
						value={value}
						thousandSeparator={true}
						decimalScale={2}
						fixedDecimalScale={true}
						prefix={"$"}
						isNumericString={true}
						displayType="text"
					/>
				),
			},
			{
				Header: "Date",
				accessor: "finalDueDate",
			},
			{
				Header: "",
				accessor: "actions",
				Cell: ({ row }) => (
					<Button
						readOnly={type !== DealStatus.PENDING}
						size="sm"
						className="btn-primary"
						onClick={() => {
							if (isLeaseOrRTO) {
								// Update lease store for lease/RTO deals
								if (selectedPeriod !== leasePeriod) {
									editLeaseField("leasePeriod", selectedPeriod);
									editLoanPeriod(selectedPeriod);
								}
								editLeaseField("term", row.original.term);
								editTerm(row.original.term);
							} else {
								// Update sale store for retail deals
								if (selectedPeriod !== loanPeriod) {
									editLoanPeriod(selectedPeriod);
								}
								editTerm(row.original.term);
							}
							toggle();
						}}
					>
						<i className="nc-icon nc-tap-01" /> Select
					</Button>
				),
			},
		],
		[type, editTerm, toggle, selectedPeriod, loanPeriod, editLoanPeriod, isLeaseOrRTO, isLease, isRTO, leasePeriod, editLeaseField]
	);

	// React Table hook
	const {
		getTableProps,
		getTableBodyProps,
		headerGroups,
		rows,
		prepareRow,
	} = useTable({ columns, data });

	return (
		<div>
			<Card>
				<CardBody className="py-0">
					<Row className="mb-3">
						<Col md="6">
							<Label>Period</Label>
							<Input
								type="select"
								value={selectedPeriod}
								onChange={(e) => setSelectedPeriod(Number(e.target.value))}
							>
								{LoanPeriod.map((period) => (
									<option key={period.value} value={period.value}>
										{period.label}
									</option>
								))}
							</Input>
						</Col>
					</Row>
					<Table responsive {...getTableProps()}>
						<thead>
							{headerGroups.map((headerGroup) => (
								<tr {...headerGroup.getHeaderGroupProps()}>
									{headerGroup.headers.map((column) => (
										<th {...column.getHeaderProps()}>
											{column.render("Header")}
										</th>
									))}
								</tr>
							))}
						</thead>
						<tbody {...getTableBodyProps()}>
							{rows.map((row) => {
								prepareRow(row);
								return (
									<tr {...row.getRowProps()}>
										{row.cells.map((cell) => (
											<td {...cell.getCellProps()}>{cell.render("Cell")}</td>
										))}
									</tr>
								);
							})}
						</tbody>
					</Table>
				</CardBody>
			</Card>
		</div>
	);
};

PaymentOptionsModal.propTypes = {
	toggle: PropTypes.func.isRequired,
};

export default PaymentOptionsModal;
